import { useState } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, Stack, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert, IconButton, Tooltip, Typography,
} from '@mui/material';
import { Add, Block, ContentCopy } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useAuth } from '../../hooks/useAuth';
import { apiKeyApi, type IssueApiKeyInput } from '../../api/integration.api';

export function ApiKeysPage() {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [issuedKey, setIssuedKey] = useState<string | null>(null);

  const { data } = useQuery({ queryKey: ['api-keys'], queryFn: () => apiKeyApi.list({ limit: 100 }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['api-keys'] });
  const revoke = useMutation({ mutationFn: (id: number) => apiKeyApi.revoke(id), onSuccess: invalidate });

  const rows = data?.data ?? [];

  return (
    <>
      <PageHeader
        title="API Keys"
        subtitle="Inbound gateway keys issued to partners. Keys are hashed; the full value is shown only once at creation."
        action={hasPermission('integration_key.create') && <Button startIcon={<Add />} variant="contained" onClick={() => setOpen(true)}>Issue Key</Button>}
      />
      <Card>
        <CardContent sx={{ p: 0, overflowX: 'auto' }}>
          {rows.length === 0 ? <EmptyState title="No API keys yet" dense /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell><TableCell>Prefix</TableCell><TableCell>Rate/min</TableCell>
                  <TableCell>IP whitelist</TableCell><TableCell>Usage</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((k) => (
                  <TableRow key={k.id} hover>
                    <TableCell>{k.name}</TableCell>
                    <TableCell><code>{k.key_prefix}…</code></TableCell>
                    <TableCell>{k.rate_limit_per_min}</TableCell>
                    <TableCell>{k.ip_whitelist?.length ? k.ip_whitelist.join(', ') : 'any'}</TableCell>
                    <TableCell>{k.usage_count}</TableCell>
                    <TableCell><Chip size="small" color={k.status === 'active' ? 'success' : 'default'} label={k.status} /></TableCell>
                    <TableCell align="right">
                      {k.status === 'active' && hasPermission('integration_key.revoke') && (
                        <Tooltip title="Revoke"><IconButton size="small" color="error" onClick={() => revoke.mutate(k.id)}><Block fontSize="small" /></IconButton></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {open && <IssueDialog onClose={() => setOpen(false)} onIssued={(key) => { setOpen(false); setIssuedKey(key); invalidate(); }} />}
      <Dialog open={Boolean(issuedKey)} onClose={() => setIssuedKey(null)} fullWidth maxWidth="sm">
        <DialogTitle>API Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Copy this key now — it will not be shown again.</Alert>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField fullWidth value={issuedKey ?? ''} InputProps={{ readOnly: true }} />
            <IconButton onClick={() => issuedKey && navigator.clipboard?.writeText(issuedKey)}><ContentCopy /></IconButton>
          </Stack>
        </DialogContent>
        <DialogActions><Button variant="contained" onClick={() => setIssuedKey(null)}>Done</Button></DialogActions>
      </Dialog>
    </>
  );
}

function IssueDialog({ onClose, onIssued }: { onClose: () => void; onIssued: (key: string) => void }) {
  const [form, setForm] = useState<{ name: string; rateLimitPerMin: string; ipWhitelist: string }>({ name: '', rateLimitPerMin: '120', ipWhitelist: '' });
  const issue = useMutation({
    mutationFn: () => {
      const input: IssueApiKeyInput = {
        name: form.name,
        rateLimitPerMin: Number(form.rateLimitPerMin) || 120,
        ipWhitelist: form.ipWhitelist.split(',').map((s) => s.trim()).filter(Boolean),
      };
      return apiKeyApi.issue(input);
    },
    onSuccess: (r) => onIssued(r.plaintextKey),
  });

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Issue API Key</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <TextField label="Rate limit (per minute)" type="number" value={form.rateLimitPerMin} onChange={(e) => setForm((f) => ({ ...f, rateLimitPerMin: e.target.value }))} />
          <TextField label="IP whitelist (comma-separated, optional)" placeholder="10.0.0.1, 192.168.1.0/24" value={form.ipWhitelist} onChange={(e) => setForm((f) => ({ ...f, ipWhitelist: e.target.value }))} />
          <Typography variant="caption" color="text.secondary">Leave IP whitelist empty to allow any source IP. Supports exact IPs and CIDR ranges.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form.name || issue.isPending} onClick={() => issue.mutate()}>Issue</Button>
      </DialogActions>
    </Dialog>
  );
}
